// The materials half of a job's variance: what was estimated against what was bought.
// Answers: did the materials cost what the estimate said, and where did they not?
//
// Bought is what this job's lines cost on their purchase orders, at the actual price once
// the invoice is in. What came off the shelf instead is shown beside it, at the estimating
// price. Shipping and tax are their own rows: the estimate has no line for them.

import { useInventoryNames } from '../inventory/names';
import { money, qty } from '../../lib/format';
import { jobVariance, useInventory } from '../../lib/inventory';
import { useSession } from '../../lib/session';
import { Empty, Info, Loading, Money, Panel, Tag } from '../../ui/kit';

export function MaterialVariance({ jobId }: { jobId: string }) {
  const s = useSession();
  const names = useInventoryNames();
  const inv = useInventory();
  if (!s.can('see_prices')) return null;
  if (inv.loading) return <Loading />;
  const job = inv.jobs.find((j) => j.job.id === jobId);
  const v = jobVariance(inv, jobId);
  const won = job?.tier === 'firm' || job?.tier === 'committed';
  const over = (cents: number) => (cents === 0 ? <span className="muted">—</span> : <span className={cents > 0 ? 'warn' : 'ok'}>{money(cents, { sign: true })}</span>);
  const rows = v ? [...v.rows].sort((a, b) => Math.abs(b.variance_cents) - Math.abs(a.variance_cents) || (inv.materialById.get(a.material_id)?.description ?? '').localeCompare(inv.materialById.get(b.material_id)?.description ?? '')) : [];
  const open = rows.filter((r) => Number(r.open_qty) > 0).length;
  const unpriced = rows.filter((r) => r.unpriced).length;

  return (
    <Panel
      label="Materials · estimated against bought"
      right={
        <>
          {v && won && (v.complete ? <Tag kind="on">purchasing complete</Tag> : <Tag kind="un">so far</Tag>)}
          <Info left>
            <b>Estimated</b> is the job's bill of materials at the estimating price (from the locked revision once there is one). <b>Bought</b> is what this job's lines cost on purchase orders: the actual price from the invoice, or the estimated price until it is
            in. <b>From stock</b> is what came off the shelf instead of being bought for the job, at the estimating price. Shipping and tax are shared out from each order by value. A material counted by the bin is not bought per job and does not appear.
          </Info>
        </>
      }
    >
      {!v || !won ? (
        <Empty>Once the job is won and its materials are bought, what they cost is laid against the estimate here.</Empty>
      ) : (
        <>
          {!v.complete && (
            <small>
              {open > 0 && `${open} ${open === 1 ? 'material is' : 'materials are'} still to buy or to arrive. `}
              {unpriced > 0 && `${unpriced} ${unpriced === 1 ? 'has' : 'have'} no actual price yet, so the estimated price stands in. `}
              The figures settle when {names.purchasing.toLowerCase()} for this job is complete.
            </small>
          )}
          <div className="tblwrap">
            <table>
              <thead>
                <tr>
                  <th>Material</th>
                  <th className="right">Estimated</th>
                  <th className="right">Est. $</th>
                  <th className="right">Bought</th>
                  <th className="right">Bought $</th>
                  <th className="right">From stock</th>
                  <th className="right">Stock $</th>
                  <th className="right">Over / under</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const m = inv.materialById.get(r.material_id);
                  return (
                    <tr key={r.material_id}>
                      <td>
                        {m?.description ?? 'a material no longer in the library'} {r.unpriced && <Tag kind="un">no actual price yet</Tag>} {Number(r.open_qty) > 0 && <Tag kind="off">{qty(r.open_qty)} still to come</Tag>}
                      </td>
                      <td className="right num">
                        {qty(r.est_qty)} <small>{m?.unit ?? ''}</small>
                      </td>
                      <td className="right">
                        <Money cents={r.est_cents} />
                      </td>
                      <td className="right num">{Number(r.bought_qty) > 0 ? qty(r.bought_qty) : '—'}</td>
                      <td className="right">{Number(r.bought_qty) > 0 ? <Money cents={r.bought_cents} /> : '—'}</td>
                      <td className="right num">{Number(r.stock_qty) > 0 ? qty(r.stock_qty) : '—'}</td>
                      <td className="right">{Number(r.stock_qty) > 0 ? <Money cents={r.stock_cents} /> : '—'}</td>
                      {/* a material still to buy or to arrive has no settled difference yet */}
                      <td className="right num">{Number(r.open_qty) > 0 ? <small>not settled</small> : over(r.variance_cents)}</td>
                    </tr>
                  );
                })}
                <tr>
                  <td>Shipping, this job's share</td>
                  <td></td>
                  <td className="right">—</td>
                  <td></td>
                  <td className="right">
                    <Money cents={v.shipping_cents} />
                  </td>
                  <td></td>
                  <td></td>
                  <td className="right num">{over(v.shipping_cents)}</td>
                </tr>
                <tr>
                  <td>Tax, this job's share</td>
                  <td></td>
                  <td className="right">—</td>
                  <td></td>
                  <td className="right">
                    <Money cents={v.tax_cents} />
                  </td>
                  <td></td>
                  <td></td>
                  <td className="right num">{over(v.tax_cents)}</td>
                </tr>
                <tr>
                  <td>
                    <b>Materials, all in</b>
                  </td>
                  <td></td>
                  <td className="right">
                    <b>
                      <Money cents={v.est_cents} />
                    </b>
                  </td>
                  <td></td>
                  <td className="right">
                    <b>
                      <Money cents={v.bought_cents + v.shipping_cents + v.tax_cents} />
                    </b>
                  </td>
                  <td></td>
                  <td className="right">
                    <b>
                      <Money cents={v.stock_cents} />
                    </b>
                  </td>
                  <td className="right num">{open > 0 ? <small>settles when everything is in</small> : <b>{over(v.variance_cents)}</b>}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </Panel>
  );
}
